import { FC, useMemo } from "react";
import { ColumnVisibilityState } from "@tanstack/react-table";
import MultiselectNestedDropdown from "@app/shared/checkbox-dropdown/MultiselectNestedDropdown";
import { CheckboxStateEnum } from "@app/shared/qes-standards/forms/Checkbox";
import { NestingParentDropdownItem } from "@app/shared/checkbox-dropdown/types";
import { PROJECT_TABLE_COLUMN_VISIBILITY_GROUPS } from "@app/network-portfolio-management/components/project-table/projectTableColumns";

const ALL_COLUMN_IDS = PROJECT_TABLE_COLUMN_VISIBILITY_GROUPS.flatMap((group) => group.columns.map((column) => column.id));

const isColumnVisible = (columnVisibility: ColumnVisibilityState, columnId: string): boolean => columnVisibility[columnId] !== false;

interface ProjectTableColumnPickerProps {
  columnVisibility: ColumnVisibilityState;
  onColumnVisibilityChange: (visibility: ColumnVisibilityState) => void;
}

/**
 * Column-visibility picker for the NPM tabular view. Adapts between MultiselectNestedDropdown's
 * "array of selected child keys" shape and TanStack's ColumnVisibilityState (Record<columnId, boolean>).
 * "Project Title" and "select" are structurally absent from PROJECT_TABLE_COLUMN_VISIBILITY_GROUPS,
 * so they never appear as picker options.
 */
export const ProjectTableColumnPicker: FC<ProjectTableColumnPickerProps> = ({
  columnVisibility,
  onColumnVisibilityChange,
}) => {
  const items = useMemo<NestingParentDropdownItem[]>(
    () =>
      PROJECT_TABLE_COLUMN_VISIBILITY_GROUPS.map((group) => ({
        checkboxStatus: CheckboxStateEnum.Unchecked,
        children: group.columns.map((column) => ({ key: column.id, name: column.label })),
        key: group.groupId,
        name: group.groupLabel,
      })),
    [],
  );

  const selectedItems = useMemo(
    () => ALL_COLUMN_IDS.filter((columnId) => isColumnVisible(columnVisibility, columnId)),
    [columnVisibility],
  );

  const handleSelectionChanged = (selectedKeys: string[]): void => {
    const selectedSet = new Set(selectedKeys);
    const nextVisibility: ColumnVisibilityState = {};
    for (const columnId of ALL_COLUMN_IDS) {
      nextVisibility[columnId] = selectedSet.has(columnId);
    }
    onColumnVisibilityChange(nextVisibility);
  };

  return (
    <MultiselectNestedDropdown
      containerClassName="network-portfolio-management-project-table-column-picker"
      items={items}
      label="Columns"
      onSelectionChanged={handleSelectionChanged}
      selectedItems={selectedItems}
      testId="network-portfolio-management-project-table-column-picker"
    />
  );
};
